import numeral from 'numeral';
import React, { useState } from 'react';
import styled from 'styled-components';
import HelpIcon from '../../assets/svg/help.svg';
import { ClaimFinancialSummaryState, ClaimFinancialSummaryStatus } from '../../containers/Claim/View/claim-financial-summary';
import { RoundButton } from '../Button';
import RepositCard from '../Reposit/RepositCard';
import { Header3, Header4, P2, P3 } from '../Typography';

interface ClaimFinancialSummaryProps {
  summaryState: ClaimFinancialSummaryState;
  totalPaid?: number;
  isTotalPaidLoading?: boolean;
  interestAmount?: number;
  originalInterestAmount?: number;
  principalAmountRemaining?: number;
  paymentPlanBalance?: number;
  isPaymentPlanBalanceLoading?: boolean;
}

interface ChevronProps {
  expanded: boolean;
}

interface TooltipBubbleProps {
  isVisible: boolean;
}

const SummaryCard = styled(RepositCard)`
  width: 100%;
`;

const SummaryBody = styled.div``;

const TooltipBubble = styled.span<TooltipBubbleProps>`
  background: ${(props) => props.theme.colors.primaryBrand};
  bottom: calc(100% + 8px);
  border-radius: 6px;
  box-shadow: 0 4px 14px rgba(58, 49, 94, 0.2);
  box-sizing: border-box;
  color: ${(props) => props.theme.colors.surface};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 13px;
  font-weight: normal;
  line-height: 1.4;
  max-width: calc(100vw - 32px);
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  padding: 12px 14px;
  pointer-events: ${(props) => (props.isVisible ? 'auto' : 'none')};
  position: absolute;
  right: 0;
  text-align: left;
  transition: opacity 0.15s ease;
  visibility: ${(props) => (props.isVisible ? 'visible' : 'hidden')};
  width: 320px;
  z-index: 10;
`;

const TooltipContainer = styled.span`
  display: inline-flex;
  margin-left: auto;
  position: relative;
`;

const TooltipButton = styled(RoundButton)`
  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.tertiaryBrand};
    outline-offset: 3px;
  }
`;

const FiguresGrid = styled.dl`
  border-bottom: 1px solid ${(props) => props.theme.colors.dividerLight};
  border-top: 1px solid ${(props) => props.theme.colors.dividerLight};
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  margin: 0;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Figure = styled.div`
  border-right: 1px solid ${(props) => props.theme.colors.dividerLight};
  padding: 20px 28px 24px;

  &:last-child {
    border-right: 0;
  }

  @media (max-width: 720px) {
    border-bottom: 1px solid ${(props) => props.theme.colors.dividerLight};
    border-right: 0;

    &:last-child {
      border-bottom: 0;
    }
  }
`;

const FigureLabel = styled(P3).attrs({ as: 'dt' })`
  margin: 0 0 4px;
`;

const FigureValue = styled(Header3).attrs({ as: 'dd' })`
  font-size: 25px;
  line-height: 1.35;
  margin: 0;
`;

const DisclosureArea = styled.div`
  padding: 18px 35px 24px;
`;

const SummaryStateMessage = styled(P2)`
  margin: 0;
  text-align: center;
`;

const ExpandButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 4px;
  color: ${(props) => props.theme.colors.primaryBrand};
  cursor: pointer;
  display: flex;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 14px;
  font-weight: 600;
  gap: 8px;
  margin: 0 auto;
  padding: 5px 8px;

  &:hover {
    background: ${(props) => props.theme.colors.bgHoverAccent};
  }

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.tertiaryBrand};
    outline-offset: 4px;
  }
`;

const Chevron = styled.span<ChevronProps>`
  border: solid currentColor;
  border-width: 0 2px 2px 0;
  display: inline-block;
  flex-shrink: 0;
  height: 7px;
  position: relative;
  top: ${(props) => (props.expanded ? '2px' : '-2px')};
  transform: rotate(${(props) => (props.expanded ? '-135deg' : '45deg')});
  transition: transform 0.15s ease;
  width: 7px;
`;

const ExpandedDetails = styled.div`
  margin-top: 22px;
`;

const DetailsExplanation = styled(P2)`
  margin: 0 0 18px;
  max-width: 760px;
`;

const Calculation = styled.dl`
  background: ${(props) => props.theme.colors.bgAccent};
  border-radius: 8px;
  margin: 0;
  padding: 6px 18px;
`;

const CalculationRow = styled.div`
  align-items: baseline;
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(0, 1fr) 120px;
  padding: 10px 0;
`;

const CalculationLabel = styled(P2).attrs({ as: 'dt' })`
  margin: 0;
`;

const CalculationOperator = styled.span`
  color: ${(props) => props.theme.colors.body};
  font-weight: bold;
  text-align: center;
`;

const CalculationValue = styled(P2).attrs({ as: 'dd' })`
  align-items: baseline;
  color: ${(props) => props.theme.colors.primaryBrand};
  display: grid;
  font-variant-numeric: tabular-nums;
  font-weight: bold;
  gap: 8px;
  grid-template-columns: 18px minmax(0, 1fr);
  margin: 0;
  text-align: right;
  width: 100%;
`;

const CalculationResultRow = styled(CalculationRow)`
  border-top: 1px solid ${(props) => props.theme.colors.dividerLight};
  margin-top: 2px;
  padding-top: 12px;
`;

const PaymentPlanSection = styled.section`
  margin-top: 22px;
`;

const SectionHeading = styled(Header4)`
  margin: 0 0 8px;
`;

const PaymentPlanCreditRow = styled(CalculationRow)`
  padding-bottom: 10px;
  padding-top: 4px;
`;

const formatAmount = (amount?: number, fixedDecimals: boolean = false) => {
  if (typeof amount !== 'number') return 'N/A';

  return `${amount < 0 ? '-' : ''}£${numeral(Math.abs(amount) / 100).format(fixedDecimals ? '0,0.00' : '0,0[.]00')}`;
};

export const ClaimFinancialSummary: React.FC<ClaimFinancialSummaryProps> = ({
  summaryState,
  totalPaid,
  isTotalPaidLoading = false,
  interestAmount,
  originalInterestAmount,
  principalAmountRemaining,
  paymentPlanBalance,
  isPaymentPlanBalanceLoading = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const isReady = summaryState.status === ClaimFinancialSummaryStatus.READY;
  const finalAmount = isReady ? summaryState.finalAmount : undefined;
  const finalAmountLabel = isReady ? summaryState.finalAmountLabel : 'Order amount';
  const explanation = summaryState.explanation;
  const unavailableValue = summaryState.status === ClaimFinancialSummaryStatus.NOT_READY ? 'Not ready' : 'Unavailable';
  const remainingAmount =
    typeof finalAmount === 'number' && typeof totalPaid === 'number' ? Math.max(0, finalAmount - totalPaid) : undefined;
  const hasCalculatedFigures = typeof remainingAmount === 'number';
  const hasPaymentPlan = typeof interestAmount === 'number';
  const originalInterest = typeof originalInterestAmount === 'number' ? originalInterestAmount : interestAmount;
  const currentPaymentPlanBalance = typeof paymentPlanBalance === 'number' ? Math.max(0, paymentPlanBalance) : undefined;
  const currentRawBalance = hasPaymentPlan ? paymentPlanBalance : principalAmountRemaining;
  const overpaymentCredit = typeof currentRawBalance === 'number' ? Math.max(0, -currentRawBalance) : 0;
  const interestOutstanding =
    hasPaymentPlan && typeof currentPaymentPlanBalance === 'number' && typeof remainingAmount === 'number'
      ? Math.max(0, currentPaymentPlanBalance - remainingAmount)
      : undefined;
  const calculatedValue = (amount?: number, fixedDecimals: boolean = false) =>
    !isReady
      ? unavailableValue
      : isTotalPaidLoading
        ? 'Calculating…'
        : hasCalculatedFigures
          ? formatAmount(amount, fixedDecimals)
          : 'Unavailable';
  const tooltip = (
    <TooltipContainer onMouseEnter={() => setIsTooltipOpen(true)} onMouseLeave={() => setIsTooltipOpen(false)}>
      <TooltipButton
        aria-describedby="payments-summary-explanation"
        aria-label="How the payments summary is calculated"
        icon={HelpIcon}
        onBlur={() => setIsTooltipOpen(false)}
        onClick={() => setIsTooltipOpen(true)}
        onFocus={() => setIsTooltipOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            setIsTooltipOpen(false);
          }
        }}
        type="button"
      />
      <TooltipBubble aria-hidden={!isTooltipOpen} id="payments-summary-explanation" isVisible={isTooltipOpen} role="tooltip">
        {explanation}
      </TooltipBubble>
    </TooltipContainer>
  );

  return (
    <SummaryCard flush headerContent={tooltip} title="Payments summary">
      <SummaryBody>
        <FiguresGrid>
          <Figure>
            <FigureLabel>Paid</FigureLabel>
            <FigureValue>{calculatedValue(totalPaid)}</FigureValue>
          </Figure>
          <Figure>
            <FigureLabel>Amount remaining</FigureLabel>
            <FigureValue>{calculatedValue(remainingAmount)}</FigureValue>
          </Figure>
          <Figure>
            <FigureLabel>Order amount</FigureLabel>
            <FigureValue>{isReady ? formatAmount(finalAmount) : unavailableValue}</FigureValue>
          </Figure>
        </FiguresGrid>
        <DisclosureArea>
          {isReady ? (
            <ExpandButton
              aria-controls="payments-summary-calculation"
              aria-expanded={isExpanded}
              onClick={() => setIsExpanded(!isExpanded)}
              type="button"
            >
              {isExpanded ? 'Hide calculation' : 'Show calculation'}
              <Chevron aria-hidden expanded={isExpanded} />
            </ExpandButton>
          ) : (
            <SummaryStateMessage role={summaryState.status === ClaimFinancialSummaryStatus.INCONSISTENT ? 'alert' : 'status'}>
              {explanation}
            </SummaryStateMessage>
          )}
          {isReady && isExpanded ? (
            <ExpandedDetails id="payments-summary-calculation">
              <DetailsExplanation>{explanation}</DetailsExplanation>
              <SectionHeading>Claim</SectionHeading>
              <Calculation>
                <CalculationRow>
                  <CalculationLabel>
                    <span>{finalAmountLabel}</span>
                  </CalculationLabel>
                  <CalculationValue>
                    <CalculationOperator aria-hidden>&nbsp;</CalculationOperator>
                    <span>{formatAmount(finalAmount, true)}</span>
                  </CalculationValue>
                </CalculationRow>
                <CalculationRow>
                  <CalculationLabel>
                    <span>Paid</span>
                  </CalculationLabel>
                  <CalculationValue>
                    <CalculationOperator aria-hidden>&minus;</CalculationOperator>
                    <span>{calculatedValue(totalPaid, true)}</span>
                  </CalculationValue>
                </CalculationRow>
                <CalculationResultRow>
                  <CalculationLabel>
                    <span>Amount remaining</span>
                  </CalculationLabel>
                  <CalculationValue>
                    <CalculationOperator aria-hidden>=</CalculationOperator>
                    <span>{calculatedValue(remainingAmount, true)}</span>
                  </CalculationValue>
                </CalculationResultRow>
                {!hasPaymentPlan && overpaymentCredit > 0 ? (
                  <PaymentPlanCreditRow>
                    <CalculationLabel>
                      <span>Overpayment / credit</span>
                    </CalculationLabel>
                    <CalculationValue>
                      <CalculationOperator aria-hidden>&nbsp;</CalculationOperator>
                      <span>{formatAmount(overpaymentCredit, true)}</span>
                    </CalculationValue>
                  </PaymentPlanCreditRow>
                ) : null}
              </Calculation>
              {hasPaymentPlan ? (
                <PaymentPlanSection>
                  <SectionHeading>Payment plan</SectionHeading>
                  <Calculation>
                    <CalculationRow>
                      <CalculationLabel>
                        <span>Amount remaining</span>
                      </CalculationLabel>
                      <CalculationValue>
                        <CalculationOperator aria-hidden>&nbsp;</CalculationOperator>
                        <span>{calculatedValue(remainingAmount, true)}</span>
                      </CalculationValue>
                    </CalculationRow>
                    <CalculationRow>
                      <CalculationLabel>
                        <span>Interest still outstanding</span>
                      </CalculationLabel>
                      <CalculationValue>
                        <CalculationOperator aria-hidden>+</CalculationOperator>
                        <span>
                          {typeof interestOutstanding === 'number'
                            ? formatAmount(interestOutstanding, true)
                            : isPaymentPlanBalanceLoading
                              ? 'Calculating…'
                              : 'Unavailable'}
                        </span>
                      </CalculationValue>
                    </CalculationRow>
                    <CalculationResultRow>
                      <CalculationLabel>
                        <span>Current payment-plan balance</span>
                      </CalculationLabel>
                      <CalculationValue>
                        <CalculationOperator aria-hidden>=</CalculationOperator>
                        <span>
                          {typeof currentPaymentPlanBalance === 'number'
                            ? formatAmount(currentPaymentPlanBalance, true)
                            : isPaymentPlanBalanceLoading
                              ? 'Calculating…'
                              : 'Unavailable'}
                        </span>
                      </CalculationValue>
                    </CalculationResultRow>
                    {overpaymentCredit > 0 ? (
                      <PaymentPlanCreditRow>
                        <CalculationLabel>
                          <span>Overpayment / credit</span>
                        </CalculationLabel>
                        <CalculationValue>
                          <CalculationOperator aria-hidden>&nbsp;</CalculationOperator>
                          <span>{formatAmount(overpaymentCredit, true)}</span>
                        </CalculationValue>
                      </PaymentPlanCreditRow>
                    ) : null}
                  </Calculation>
                </PaymentPlanSection>
              ) : typeof originalInterest === 'number' ? (
                <PaymentPlanSection>
                  <SectionHeading>Payment plan</SectionHeading>
                  <Calculation>
                    <CalculationRow>
                      <CalculationLabel>
                        <span>Original interest</span>
                      </CalculationLabel>
                      <CalculationValue>
                        <CalculationOperator aria-hidden>&nbsp;</CalculationOperator>
                        <span>{formatAmount(originalInterest, true)}</span>
                      </CalculationValue>
                    </CalculationRow>
                  </Calculation>
                </PaymentPlanSection>
              ) : null}
            </ExpandedDetails>
          ) : null}
        </DisclosureArea>
      </SummaryBody>
    </SummaryCard>
  );
};

export default ClaimFinancialSummary;
